import { Backdrop, Box, Button, CircularProgress, Grid2, Snackbar, Typography } from "@mui/material";
import { DataGrid, gridClasses, GridRowEditStopReasons, GridRowModes, } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import EditButton from "./EditButton.js";
import CancelButton from "./CancelButton.js";
import SaveButton from "./SaveButton.js";
import { validateEmail, validateUsername } from "../../utilities/validation.js";
import HeaderBar from "../Shared/HeaderBar/HeaderBar.js";
import NavigationBar from "../Shared/NavigationBar/NavigationBar.js"
import { useAuth0 } from "@auth0/auth0-react";
import { getApiUrl } from "../../utilities/consts.js";
import ApplyChangesDialog from "./ApplyChangesDialog.js";


/**
 * Gets the fields that have been modified between before and after
 * @param {object} before Object before changes
 * @param {object} after Object after changes
 * @returns {object} An object with only the fields that have been changed between before and after
 */
function getModifiedFields(before, after) {
  const ret = structuredClone(after);

  // removes all fields that are the same in before and after
  if (before) {
    for (const key in after) {
      if (before[key] === after[key]) delete ret[key];
    }
  }

  return ret;
}

/**
 * Fetches user and role data
 * @param {string} accessToken API access token
 * @returns {object[]} Array with user data in index 0 and role data in index 1
 */
async function fetchUserAndRoleData(accessToken) {
  const apiUrl = getApiUrl();
  const userDataPromise = fetch(`${apiUrl}/user`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });
  const roleDataPromise = fetch(`${apiUrl}/user/role`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });
  const [userDataRes, roleDataRes] = await Promise.all([userDataPromise, roleDataPromise]);
  const [userData, roleData] = await Promise.all([userDataRes.json(), roleDataRes.json()]);
  if (!userDataRes.ok) throw new Error(userData);
  if (!roleDataRes.ok) throw new Error(roleData);
  return [userData, roleData];
}

export default function AdminPage() {
  const { getAccessTokenSilently } = useAuth0();

  // changes that have been made but not yet applied
  const [pendingChanges, setPendingChanges] = useState({});
  // rows that are displayed in the data grid
  const [userRows, setUserRows] = useState(null);
  // what mode each of the rows are in (edit or view)
  const [rowModesModel, setRowModesModel] = useState({});
  // error to display
  const [error, setError] = useState(null);
  // role information (name, description, id)
  const [roles, setRoles] = useState([]);
  // if the edit confirmation dialog is open
  const [confirmOpen, setConfirmOpen] = useState(false);
  // if the loading overlay is displayed
  const [loading, setLoading] = useState(false);

  const columns = [
    {
      field: "role",
      headerName: "Role",
      headerAlign: 'center',
      textAlign: 'center',
      flex: 1,
      editable: true,
      type: "singleSelect",
      valueOptions: roles.map((role) => role.name),
    },
    {
      field: "username",
      headerName: "Username",
      headerAlign: 'center',
      textAlign: 'center',
      flex: 1.5,
      editable: true,
      preProcessEditCellProps: ({ props }) => {
        const isValidUsername = validateUsername(props.value);
        return { ...props, error: !isValidUsername };
      },
    },
    {
      field: "email",
      headerName: "Email",
      headerAlign: 'center',
      textAlign: 'center',
      flex: 2,
      editable: false,
      preProcessEditCellProps: ({ props }) => {
        const isValidEmail = validateEmail(props.value);
        return { ...props, error: !isValidEmail };
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      headerAlign: 'center',
      textAlign: 'center',
      cellClassName: "actions",
      flex: 2,
      getActions: ({ id: rowID }) => getActionButtons(rowID),
    },
  ];

  // get user and role data
  useEffect(() => {
    (async () => {
      try {
        const accessToken = await getAccessTokenSilently({ authorizationParams: { scope: "read:users read:roles" } });
        const [userData, roleData] = await fetchUserAndRoleData(accessToken);
        setUserRows(userData);
        setRoles(roleData);
      } catch (error) {
        setError("Error fetching user and role data");
        console.error("Error fetching user and role data:", error);
      }
    })();
  }, [getAccessTokenSilently]);

  // returns an array of what action buttons should be in a row
  const getActionButtons = (rowID) => {
    if (rowModesModel[rowID]?.mode === GridRowModes.Edit)
      return [
        <SaveButton rowID={rowID} setRowModesModel={setRowModesModel} />,
        <CancelButton rowID={rowID} setRowModesModel={setRowModesModel} />,
      ];
    else
      return [<EditButton rowID={rowID} setRowModesModel={setRowModesModel} />];
  };

  const processRowUpdate = (updatedRow, previousRow) => {
    // add updates to pending changes if there were changes
    const rowUpdates = getModifiedFields(previousRow, updatedRow);
    if (Object.keys(rowUpdates).length > 0) {
      const prevState = pendingChanges[updatedRow.user_id];
      setPendingChanges({ ...pendingChanges, ...{ [updatedRow.user_id]: { ...prevState, ...rowUpdates } } })
    }
    return updatedRow;
  };

  const handleProcessRowUpdateError = (error) => {
    console.log(error);
    setError("Error updating row");
  }

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const getRowClassName = ({ id }) => {
    // add the edited class to any rows that have changes made to them
    return pendingChanges[[id]] ? "edited" : "";
  };

  const resetCurrentChanges = () => {
    // clear any changes made to the rows
    setPendingChanges({});
  };

  // sends changes to the backend and updates rows
  const sendChanges = async (changesBody) => {
    const accessToken = await getAccessTokenSilently({ authorizationParams: { scope: "write:users read:users read:roles" } });

    const sendChangesRes = await fetch(`${getApiUrl()}/user`, {
      method: "PATCH",
      headers: {
        "content-type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(changesBody),
    });
    if (!sendChangesRes.ok) {
      const data = await sendChangesRes.json();
      setError("Error sending changes");
      console.error("Error sending changes:", data);
    } else {
      setUserRows(userRows?.map((row) => ({ ...row, ...pendingChanges[row.user_id] })));
      setPendingChanges({});
    }
    setLoading(false);
  };

  // opens the confirmation box
  const confirmChanges = () => {
    setConfirmOpen(false);
    setLoading(true);
    const changesBody = [];
    for (const [userId, changes] of Object.entries(pendingChanges)) {
      // create entry to changes array to send to api
      const changesBodyEntry = { user_id: userId, ...changes };
      // get role id from role name, and remove old role field
      if (changesBodyEntry.role) {
        changesBodyEntry.role_id = roles.find((role) => role.name === changes.role).id;
        delete changesBodyEntry.role;
      }
      changesBody.push(changesBodyEntry);
    }

    sendChanges(changesBody);
  };

  const handleRowEditStop = (params, event) => {
    // disable exiting edit mode if user clicks off of row
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };


  return (
    <>
      <HeaderBar></HeaderBar>
      <Backdrop open={loading} zIndex={999}><CircularProgress/></Backdrop>
      <ApplyChangesDialog
        userData={userRows}
        pendingChanges={pendingChanges}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onApply={confirmChanges}
      />
      <Grid2
        container={true}
        alignItems={'center'}
        direction={'column'}
        id={"AdminPageGrid"}
        sx={{
          width: "100vw",
        }}
      >
        <Typography variant={"h5"} textAlign={'center'}>Member Access & Roles</Typography>
        <DataGrid
          sx={{
            [`.${gridClasses.row}.edited`]: {color: "red",},
            width: "100%",
            maxWidth: "800px"
          }}
          rows={userRows?.map((row) => ({ ...row, ...pendingChanges[row.user_id] }))}
          getRowId={(row) => row.user_id}
          columns={columns}
          rowModesModel={rowModesModel}
          editMode="row"
          processRowUpdate={processRowUpdate}
          getRowClassName={getRowClassName}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          loading={!userRows}
        />
        {/* show reset/apply buttons only if there have been changes to a row */}
        {Object.keys(pendingChanges).length > 0 && (
          <Box display={"flex"} paddingTop={1} gap={1}>
            <Button onClick={resetCurrentChanges} variant={"contained"}>
              Reset
            </Button>
            <Button onClick={() => setConfirmOpen(true)} variant={"contained"}>
              Apply
            </Button>
          </Box>
        )}
        <Snackbar
          open={error}
          autoHideDuration={3000}
          message={error}
          onClose={() => setError(null)}
        />
      </Grid2>
      <NavigationBar></NavigationBar>
    </>
  );
}
